import { useMemo } from "react";
import { StatusBadge, BOOKING_STATUS_META } from "../../lib/status";
import { VehicleStateBlock } from "./VehicleStateBlock";
import { ConflictBadge, BufferWarningBadge } from "./ConflictBadge";

const ROW_HEIGHT = 56;
const DAY_MS = 24 * 3600 * 1000;

function pct(date, rangeStartMs, totalMs) {
  const t = new Date(date).getTime();
  return Math.max(0, Math.min(100, ((t - rangeStartMs) / totalMs) * 100));
}

function dayLabel(d) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Rows = vehicles, columns = time. Every block is derived from the exact
 * same production data used everywhere else in ConfirmPH (bookings,
 * vehicle.status, conflict_service): nothing here is a calendar-only
 * concept. Horizontally scrollable so long ranges/large fleets stay
 * readable instead of squeezing into the viewport (Phase 2F mobile
 * requirement: also benefits desktop on 30-day views). */
export function CalendarGrid({ vehicles, rangeStart, rangeEnd, onOpenBooking, onOpenIssue,
                                onEmptySlot, onOpenVehicle }) {
  const rangeStartMs = rangeStart.getTime();
  const totalMs = rangeEnd.getTime() - rangeStart.getTime();
  const days = Math.max(1, Math.round(totalMs / DAY_MS));
  const minWidth = Math.max(560, days * 90);

  const dayMarks = useMemo(() => {
    const marks = [];
    for (let t = new Date(rangeStart); t < rangeEnd; t = new Date(t.getTime() + DAY_MS)) {
      marks.push({ date: new Date(t), left: pct(t, rangeStartMs, totalMs) });
    }
    return marks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart.getTime(), rangeEnd.getTime()]);

  const now = new Date();
  const nowLeft = now >= rangeStart && now <= rangeEnd ? pct(now, rangeStartMs, totalMs) : null;

  const handleRowClick = (vehicle, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const clickedMs = rangeStartMs + frac * totalMs;
    const clicked = new Date(clickedMs);
    // Default a 48h window (matches BookingNew.jsx's own default duration),
    // capped at whichever comes first: the next booking on this vehicle, or
    // the visible range's end.
    const nextBookingStart = (vehicle.blocks || [])
      .filter((b) => b.type === "booking" && new Date(b.start).getTime() > clickedMs)
      .map((b) => new Date(b.start).getTime())
      .sort((a, b) => a - b)[0];
    let end = clickedMs + 48 * 3600 * 1000;
    if (nextBookingStart && nextBookingStart < end) end = nextBookingStart;
    onEmptySlot(vehicle.id, clicked, new Date(end));
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="fc-calendar-grid">
      <div className="overflow-x-auto">
        <div style={{ minWidth: `${minWidth}px` }}>
          {/* Day header */}
          <div className="relative h-8 border-b border-slate-200 bg-slate-50" style={{ marginLeft: 200 }}>
            {dayMarks.map((m) => (
              <div key={m.date.toISOString()} className="absolute top-0 bottom-0 flex items-center px-2 text-[11px] font-semibold text-slate-500 border-l border-slate-200"
                   style={{ left: `${m.left}%` }}>
                {dayLabel(m.date)}
              </div>
            ))}
          </div>

          {vehicles.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="fc-grid-empty">
              No vehicles match the current filters.
            </div>
          )}

          {vehicles.map((v) => (
            <div key={v.id} className="border-b border-slate-100 last:border-b-0" data-testid={`fc-row-${v.id}`}>
            <div className="flex">
              <button
                type="button"
                onClick={() => onOpenVehicle(v.id)}
                data-testid={`fc-row-label-${v.id}`}
                className="w-[200px] flex-shrink-0 text-left px-3 py-2 hover:bg-slate-50 border-r border-slate-100"
                style={{ height: ROW_HEIGHT }}
              >
                <div className="text-sm font-medium text-slate-900 truncate">{v.name}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[11px] text-slate-500 font-mono truncate">{v.plate_number}</span>
                  <StatusBadge status={v.status} type="vehicle" />
                </div>
              </button>

              <div
                className="relative flex-1 cursor-cell hover:bg-slate-50/60"
                style={{ height: ROW_HEIGHT }}
                onClick={(e) => handleRowClick(v, e)}
                data-testid={`fc-track-${v.id}`}
              >
                {dayMarks.map((m) => (
                  <div key={m.date.toISOString()} className="absolute top-0 bottom-0 border-l border-slate-100"
                       style={{ left: `${m.left}%` }} />
                ))}
                {nowLeft !== null && (
                  <div className="absolute top-0 bottom-0 w-px bg-rose-400 z-10" style={{ left: `${nowLeft}%` }} />
                )}

                {(v.blocks || []).map((b, idx) => {
                  if (b.type === "vehicle_status") {
                    const left = pct(b.start || rangeStart, rangeStartMs, totalMs);
                    return (
                      <VehicleStateBlock
                        key={`vs-${idx}`} block={b} left={left} width={100 - left}
                        onClick={() => onOpenVehicle(v.id)}
                        testId={`fc-block-status-${v.id}`}
                      />
                    );
                  }
                  const left = pct(b.start, rangeStartMs, totalMs);
                  const right = pct(b.end, rangeStartMs, totalMs);
                  const width = Math.max(1.2, right - left);
                  const meta = BOOKING_STATUS_META[b.subtype] || BOOKING_STATUS_META.draft;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenBooking(b.id); }}
                      data-testid={`fc-block-booking-${b.id}`}
                      title={`${b.customer_name}: ${meta.label}`}
                      style={{ left: `${left}%`, width: `${width}%` }}
                      className={`absolute top-1 bottom-1 rounded-md border text-[10px] font-semibold px-1.5 flex items-center overflow-hidden ${meta.bg} ${meta.text} ${meta.border}`}
                    >
                      <span className="truncate">{b.customer_name}</span>
                    </button>
                  );
                })}

              </div>
            </div>

            {/* Conflict / buffer indicators: a normal (non-overlapping) sub-row,
                only present when there's something to flag, aligned under the track. */}
            {(v.state_conflicts?.length > 0 || v.overlap_conflicts?.length > 0 || v.buffer_warnings?.length > 0) && (
              <div className="flex flex-wrap gap-1 px-2 py-1 bg-red-50/40" style={{ marginLeft: 200 }}
                   data-testid={`fc-issues-${v.id}`}>
                {v.state_conflicts?.length > 0 && (
                  <ConflictBadge kind="state" count={v.state_conflicts.length}
                                 onClick={() => onOpenIssue(v.state_conflicts[0].booking_id)}
                                 testId={`fc-conflict-state-${v.id}`} />
                )}
                {v.overlap_conflicts?.map((c, i) => (
                  <ConflictBadge key={`ov-${i}`} kind="overlap" count={1}
                                 onClick={() => onOpenIssue(c.booking_a.id)}
                                 testId={`fc-conflict-overlap-${v.id}-${i}`} />
                ))}
                {v.buffer_warnings?.map((w, i) => (
                  <BufferWarningBadge key={`bw-${i}`} warning={w}
                                       onClick={() => onOpenIssue(w.booking_a.id)}
                                       testId={`fc-buffer-${v.id}-${i}`} />
                ))}
              </div>
            )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
